/**
 * What a save's calculation found (KPR-828; epic KPE-027, built on KPR-686).
 *
 * keepr works out a group's calculated columns, and the calculated elements
 * that read a group, in the same write as the entries (keepr-api docs/SCHEMA.md
 * "Formulas over a repeating group"). Two things can come back that the form
 * must say in words:
 *
 *   - a write that would need more work than one save allows is refused,
 *     `400 formula_too_costly { element?, column? }`, with a sentence of its
 *     own, and nothing is written;
 *   - a value a calculation could not work out (a division by zero, a result
 *     outside a choice column's choices) is stored blank and the 200 carries
 *     `warnings: [{ code: 'formula_warning', element, column?, position?,
 *     entryId?, message }]` — `position` from 1 with headings counted. On the
 *     single-entry doors they ride the answer's `item` (`item.warnings`).
 *
 * Pure: no React, no DOM, no network. Other warning codes (the tag ones) are
 * not this file's and are never touched here.
 */

import { columnsOf, isRepeatingGroup } from '../config/repeatingGroups';
import { entriesOf, entryKey, entryKeyFromRefusal, entryName, isHeading, nounsOf } from './repeatingGroupEntries';

export const FORMULA_WARNING = 'formula_warning';
export const FORMULA_TOO_COSTLY = 'formula_too_costly';

/** At most this many warnings are spelled out; the rest are counted. */
export const WARNING_NOTES_SHOWN = 3;

export const isFormulaWarning = warning => warning?.code === FORMULA_WARNING;

/** The formula warnings an answer carries (a write's body, or a door's `item`). */
export const formulaWarningsOf = answer => (Array.isArray(answer?.warnings) ? answer.warnings : []).filter(isFormulaWarning);

/** Is this a refused save for the cost of its calculations? */
export const isFormulaTooCostly = error => (
	error?.response?.status === 400 && error.response.data?.code === FORMULA_TOO_COSTLY
);

const sameWarning = warning => [
	warning.element || '',
	warning.column || '',
	warning.entryId || (Number.isInteger(warning.position) ? `p${warning.position}` : ''),
].join('|');

/**
 * Every formula warning a save collected, once each. A save can be several
 * writes (the PUT, then one door per entry change); each answer says what ITS
 * calculation found, so a later answer about the same value replaces an earlier
 * one. `finalElements` is the item as the last answer served it, when there is
 * one: a warning about an entry that is no longer in the list it names is
 * dropped (the entry was removed by a later change).
 */
export function mergeFormulaWarnings(batches, finalElements = null) {
	const merged = new Map();
	(Array.isArray(batches) ? batches : []).forEach((batch) => {
		(Array.isArray(batch) ? batch : []).filter(isFormulaWarning).forEach((warning) => {
			merged.set(sameWarning(warning), warning);
		});
	});
	return [...merged.values()].filter((warning) => {
		if (!warning.entryId || !finalElements) return true;
		const list = finalElements[warning.element];
		if (!Array.isArray(list)) return true;
		return list.some(entry => entry?.id === warning.entryId);
	});
}

const clean = text => String(text || '').trim().replace(/[.\s]+$/, '');
const labelOf = def => def?.label?.singular || def?.name || '';

/**
 * One warning as a sentence that says where it is: the group, the entry as the
 * form numbers it ("ingredient 3"; found by `entryId`, else by `position`), the
 * column, and keepr's own words for what went wrong.
 *
 * `defs` are the card's element definitions as the form holds them; `elements`
 * the form's values (the list the entry numbers are counted in).
 */
export function formulaWarningNote(warning, { defs = [], elements = {} } = {}) {
	const message = clean(warning?.message) || 'it could not be worked out';
	const def = (defs || []).find(candidate => candidate?.name === warning?.element) || null;
	if (!def) return `${warning?.element || 'A calculation'}: ${message}.`;
	if (!isRepeatingGroup(def)) return `${labelOf(def)}: ${message}.`;

	const { pluralLabel } = nounsOf(def);
	const column = warning.column ? columnsOf(def).find(candidate => candidate?.name === warning.column) : null;
	const parts = [pluralLabel];
	if (warning.entryId || Number.isInteger(warning.position)) {
		const list = entriesOf(elements?.[def.name]);
		const key = entryKeyFromRefusal(list, warning);
		const found = key ? list.find(entry => entryKey(entry) === key) : null;
		parts.push(found && !isHeading(found) ? entryName(def, list, found) : `entry ${warning.position ?? ''}`.trim());
	}
	if (warning.column) parts.push(column ? labelOf(column) : warning.column);
	return `${parts.join(', ')}: ${message}.`;
}

/**
 * What the form says after a save whose calculation found something: one
 * sentence leading, then the first few warnings in words, then a count of the
 * rest. '' when there is nothing to say.
 */
export function formulaWarningsNotice(warnings, context) {
	const list = (Array.isArray(warnings) ? warnings : []).filter(isFormulaWarning);
	if (!list.length) return '';
	const lead = list.length === 1
		? 'A calculated value could not be worked out.'
		: `${list.length} calculated values could not be worked out.`;
	const shown = list.slice(0, WARNING_NOTES_SHOWN).map(warning => formulaWarningNote(warning, context));
	const rest = list.length - shown.length;
	return [lead, ...shown, rest > 0 ? `And ${rest} more.` : ''].filter(Boolean).join(' ');
}
